import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import GoogleButton from "@/components/GoogleButton";
import RequiredMark from "@/components/RequiredMark";

function Req() {
    return <RequiredMark />;
}

// Safe redirect target — only allow same-origin relative paths.
function safeRedirect(raw) {
    if (!raw || typeof raw !== "string") return null;
    if (!raw.startsWith("/") || raw.startsWith("//")) return null;
    return raw;
}

export default function Register() {
    const { register } = useAuth();
    const nav = useNavigate();
    const [params] = useSearchParams();
    const redirectTo = safeRedirect(params.get("redirect"));
    const [form, setForm] = useState({
        name: "",
        email: "",
        password: "",
        organization: "",
        accept_privacy: false,
    });
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    const set = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setErr("");
        if (!form.accept_privacy) {
            setErr("Please accept the privacy notice to continue.");
            return;
        }
        if (form.password.length < 8) {
            setErr("Password must be at least 8 characters.");
            return;
        }
        setLoading(true);
        try {
            await register(form);
            nav(redirectTo || "/dashboard");
        } catch (e) {
            setErr(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 grid grid-cols-12 gap-10" data-testid="register-page">
            <div className="col-span-12 md:col-span-5">
                <div className="overline mb-4">Begin</div>
                <h1 className="font-display text-4xl md:text-5xl leading-tight">Create your consultant account</h1>
                <p className="text-[#c2c2c2] mt-6 leading-relaxed max-w-md">
                    One account per consultant. Each scan you run for a client
                    lives in your dashboard until you erase it.
                </p>
                <div className="mt-8 border border-[#2a2a2a] p-5 bg-[#141414]">
                    <div className="overline mb-3">Privacy at a glance</div>
                    <ul className="space-y-1 text-sm text-[#d4d4d4]">
                        <li>· Client responses stay confidential to you.</li>
                        <li>· We never share with Microsoft or any reseller.</li>
                        <li>· GDPR rights honoured: access, rectify, erase, export.</li>
                        <li>· One-click delete per scan from your dashboard.</li>
                    </ul>
                </div>
            </div>
            <div className="col-span-12 md:col-span-7">
                <form
                    onSubmit={onSubmit}
                    className="border border-[#2a2a2a] bg-[#141414] p-8 max-w-xl"
                    data-testid="register-form"
                >
                    <GoogleButton label="Sign up with Google" />
                    <div className="flex items-center gap-3 my-6">
                        <div className="flex-1 h-px bg-[#2a2a2a]" />
                        <span className="text-[13px] uppercase tracking-[0.12em] text-[#c2c2c2] font-semibold">
                            or with email
                        </span>
                        <div className="flex-1 h-px bg-[#2a2a2a]" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                            <label className="label" htmlFor="reg-name">Full name<Req /></label>
                            <input
                                id="reg-name"
                                className="input-base"
                                value={form.name}
                                onChange={set("name")}
                                aria-required="true"
                                required
                                data-testid="register-name-input"
                            />
                        </div>
                        <div>
                            <label className="label" htmlFor="reg-org">Organization (optional)</label>
                            <input
                                id="reg-org"
                                className="input-base"
                                value={form.organization}
                                onChange={set("organization")}
                                data-testid="register-org-input"
                            />
                        </div>
                    </div>
                    <div className="mb-4">
                        <label className="label" htmlFor="reg-email">Work email<Req /></label>
                        <input
                            id="reg-email"
                            type="email"
                            className="input-base"
                            value={form.email}
                            onChange={set("email")}
                            aria-required="true"
                            required
                            data-testid="register-email-input"
                        />
                    </div>
                    <div className="mb-5">
                        <label className="label" htmlFor="reg-password">Password (min 8 characters)<Req /></label>
                        <input
                            id="reg-password"
                            type="password"
                            className="input-base"
                            value={form.password}
                            onChange={set("password")}
                            aria-required="true"
                            required
                            minLength={8}
                            data-testid="register-password-input"
                        />
                    </div>
                    <label className="flex items-start gap-3 text-sm text-[#d4d4d4] mb-6 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.accept_privacy}
                            onChange={set("accept_privacy")}
                            className="mt-1"
                            data-testid="register-privacy-checkbox"
                        />
                        <span>
                            I acknowledge SmartNote2Self's GDPR-respectful handling of my data
                            and confirm I can delete my account and assessments at any time.<Req />
                        </span>
                    </label>
                    {err && (
                        <div className="block-risk text-sm mb-4" data-testid="register-error">
                            {err}
                        </div>
                    )}
                    <button
                        type="submit"
                        className="btn-primary w-full md:w-auto"
                        disabled={loading}
                        data-testid="register-submit-button"
                    >
                        {loading ? "Creating account…" : "Create account"}
                    </button>
                    <div className="mt-6 text-sm text-[#c2c2c2]">
                        Already have an account?{" "}
                        <Link
                            to={redirectTo ? `/login?redirect=${encodeURIComponent(redirectTo)}` : "/login"}
                            className="text-[#00A896] hover:underline"
                            data-testid="register-to-login-link"
                        >
                            Sign in
                        </Link>
                    </div>
                </form>
            </div>
        </div>
    );
}
